import React, { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Listbox, Transition } from '@headlessui/react';
import { FaChevronDown } from 'react-icons/fa';

function findOption(options, value) {
    if (!options?.length) return undefined;
    const direct = options.find((o) => Object.is(o.value, value));
    if (direct) return direct;
    return options.find((o) => String(o.value) === String(value));
}

/** True in browsers that support HTML5 form constraint validation (checkValidity / :invalid). */
function supportsHtml5FormValidation() {
    if (typeof document === 'undefined') return true;
    try {
        const el = document.createElement('input');
        return typeof el.checkValidity === 'function';
    } catch {
        return false;
    }
}

/**
 * Fixed panel aligned to the trigger: stays above other UI (high z-index) and does not
 * participate in scroll-container clipping or layout flow the way absolute-in-cell does.
 */
function ListboxOptionsPanel({ open, triggerRef, children, panelClassName }) {
    const [panelStyle, setPanelStyle] = useState({ top: 0, left: 0, width: 0 });

    useLayoutEffect(() => {
        if (!open) return undefined;

        const update = () => {
            const el = triggerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            setPanelStyle({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
            });
        };

        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open, triggerRef]);

    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
        >
            <Listbox.Options
                className={panelClassName}
                style={{
                    position: 'fixed',
                    top: panelStyle.top,
                    left: panelStyle.left,
                    width: panelStyle.width,
                    zIndex: 10050,
                }}
            >
                {children}
            </Listbox.Options>
        </Transition>,
        document.body
    );
}

/**
 * Custom dropdown: flat shaded surfaces (no gradients), rounded trigger + panel,
 * options animate open with a short slide + fade (Headless UI Listbox + Transition).
 *
 * Uses a native `<select>` when:
 * - `native` is explicitly `true`, or
 * - `native` is undefined and (`required` is set, or the browser has no HTML5 constraint validation API).
 *
 * Pass `native={false}` to always use the custom control (e.g. animated list) even when `required`;
 * note that browser `:invalid` / submit blocking will not apply to the custom control.
 */
const ShadedSelect = ({
    value,
    onChange,
    options = [],
    disabled = false,
    id,
    name,
    className = '',
    buttonClassName = '',
    size = 'md',
    error = false,
    required = false,
    onBlur,
    /** `true` = always native `<select>`. `false` = always custom Listbox. `undefined` = auto (see module doc). */
    native,
    /** Hide the trailing chevron (e.g. narrow DSE grade fields). */
    hideChevron = false,
    /** Open the custom listbox as soon as its trigger mounts. */
    autoOpen = false,
    onAutoOpen,
    'aria-label': ariaLabel,
}) => {
    const selected = useMemo(() => findOption(options, value), [options, value]);
    const triggerRef = useRef(null);
    const triggerButtonRef = useRef(null);

    const useNativeSelect =
        native === true ? true : native === false ? false : !supportsHtml5FormValidation() || required;

    // Heights align with formUiClasses.formInputClass (h-10 / h-8 for sm)
    const sizeClasses =
        size === 'sm'
            ? `h-8 py-0 ${hideChevron ? 'pl-2.5 pr-2.5' : 'pl-2.5 pr-8'} text-xs`
            : `h-10 py-0 ${hideChevron ? 'pl-3 pr-3' : 'pl-3 pr-9'} text-sm`;

    const borderError = error ? 'border-red-400 ring-red-200' : 'border-edge-strong';

    const sharedFieldClass = `
        relative box-border w-full rounded-lg border text-left shadow-sm
        bg-surface-muted text-ink
        ${borderError}
        focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0
        disabled:cursor-not-allowed disabled:opacity-50
        ${sizeClasses}
        ${buttonClassName}
    `;

    const handleNativeChange = (e) => {
        const raw = e.target.value;
        const opt = options.find((o) => String(o.value) === raw);
        onChange(opt ? opt.value : raw);
    };

    const panelClassName =
        'max-h-96 overflow-auto rounded-lg border border-edge bg-surface-elevated shadow-lg focus:outline-none';

    useLayoutEffect(() => {
        if (!autoOpen || useNativeSelect || disabled) return;
        triggerButtonRef.current?.click();
        onAutoOpen?.();
    }, [autoOpen, disabled, onAutoOpen, useNativeSelect]);

    if (useNativeSelect) {
        const selectValue = value === undefined || value === null ? '' : value;
        return (
            <div className={`relative ${className}`}>
                <select
                    id={id}
                    name={name}
                    value={selectValue}
                    onChange={handleNativeChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    required={required}
                    aria-label={ariaLabel}
                    className={`${sharedFieldClass} block cursor-pointer appearance-none`}
                >
                    {options.map((opt) => (
                        <option key={String(opt.value)} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                {!hideChevron ? (
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <FaChevronDown className="h-3 w-3 text-ink-subtle" aria-hidden />
                </span>
                ) : null}
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            <Listbox value={value} onChange={onChange} disabled={disabled}>
                {({ open }) => (
                    <>
                        <div ref={triggerRef} className="relative w-full">
                            <Listbox.Button
                                ref={triggerButtonRef}
                                id={id}
                                name={name}
                                aria-label={ariaLabel}
                                aria-required={required || undefined}
                                onBlur={onBlur}
                                className={`${sharedFieldClass} w-full cursor-pointer`}
                            >
                <span className="block truncate">
                                    {selected ? selected.label : '\u00a0'}
                                </span>
                                {!hideChevron ? (
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <FaChevronDown
                                        className={`h-3 w-3 text-ink-subtle transition-transform ${open ? 'rotate-180' : ''}`}
                                        aria-hidden
                                    />
                                </span>
                                ) : null}
                            </Listbox.Button>
                        </div>

                        <ListboxOptionsPanel open={open} triggerRef={triggerRef} panelClassName={panelClassName}>
                            {options.map((opt, index) => {
                                const isFirst = index === 0;
                                const isLast = index === options.length - 1;
                                const verticalPad =
                                    isFirst && isLast
                                        ? 'py-3'
                                        : isFirst
                                          ? 'pt-3 pb-2'
                                          : isLast
                                            ? 'pt-2 pb-3'
                                            : 'py-2';
                                const cornerRadius =
                                    isFirst && isLast
                                        ? 'rounded-lg'
                                        : isFirst
                                          ? 'rounded-t-lg'
                                          : isLast
                                            ? 'rounded-b-lg'
                                            : '';
                                return (
                                <Listbox.Option
                                    key={String(opt.value)}
                                    value={opt.value}
                                    className={({ active, selected: isSelected }) =>
                                        `relative cursor-pointer select-none px-3 text-sm ${verticalPad} ${cornerRadius} ${
                                            isSelected
                                                ? 'bg-accent/30 font-medium text-ink'
                                                : active
                                                  ? 'bg-surface-muted text-ink'
                                                  : 'text-ink-muted'
                                        }`
                                    }
                                >
                                    {opt.label}
                                </Listbox.Option>
                                );
                            })}
                        </ListboxOptionsPanel>
                    </>
                )}
            </Listbox>
        </div>
    );
};

export default ShadedSelect;
